import { Bars3Icon, EyeIcon, EyeSlashIcon, PencilSquareIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { setMenu } from 'actions'
import { LayoutLoading } from 'components/LayoutLoading'
import { Sortable } from 'components/Sortable'
import { cloneDeep } from 'lodash'
import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { getAdminConfig, setAdminConfig } from 'services'
import { Button, Tooltip } from 'stories/components'
import { confirm, convertToTitleCase } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { InterfaceAddSectionModal } from './InterfaceAddSectionModal'
import { RegisterFormConfiguration } from './RegisterFormConfiguration'

interface InterfaceItem {
  title: string
  link?: string
  value: boolean
  order: number
  internal?: boolean
}

// A logo belongs to a portal. Servicing mode is the staff portal in another mode, and the account
// dropdowns hang off a portal that already has one, so neither gets a field of its own.
const NO_LOGO_SECTIONS = [
  'servicingPortal',
  'accountMenu',
  'borrowerMenu',
  'investorMenu',
  'participantMenu',
]

const HELPER_TEXT: Record<string, string> = {
  default: 'Drag to set the order these appear in the menu, and hide the ones this company does not use.',
  brokerPortal:
    'The top menu in Origination mode. Drag to set the order, and hide the ones this company does not use. A user still sees only the ones their permissions allow.',
  servicingPortal:
    'The top menu in Servicing mode, in full: drag any of these anywhere in the list, and hide the ones this company does not use. A user still sees only the ones their permissions allow.',
  accountMenu:
    'The dropdown behind the menu button, for signed-in staff. Drag to set the order, and hide the ones this company does not use. A user still sees only the ones their permissions allow.',
  borrowerMenu: 'The dropdown behind the menu button in the borrower portal.',
  investorMenu: 'The dropdown behind the menu button in the investor portal.',
  participantMenu: 'The dropdown behind the menu button in the participant portal.',
  participantPortal:
    'The top menu in the participant portal. Distributions, Funds and Loan Purchases are also hidden from a participant who holds none of that side of the portal.',
  vendorPortal: 'The top menu in the vendor portal.',
}

export const Interface = () => {
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(false)
  const [data, setData] = useState<Record<string, Record<string, InterfaceItem>>>({})
  const [selectedPortal, setSelectedPortal] = useState<string>('')
  const [editData, setEditData] = useState<{ key: string; data: InterfaceItem } | undefined>(undefined)
  const [showRegisterFormConfig, setShowRegisterFormConfig] = useState(false)

  const initData = async () => {
    setIsLoading(true)
    const res = await getAdminConfig('interface')

    // Define portal order: each portal's top menu, then the account dropdown that belongs with it.
    const portalOrder = [
      'homePortal',
      'brokerPortal',
      'servicingPortal',
      'accountMenu',
      'borrowerPortal',
      'borrowerMenu',
      'investorPortal',
      'investorMenu',
      'participantPortal',
      'participantMenu',
      'vendorPortal',
    ]

    // Create new ordered object
    const orderedRes: Record<string, Record<string, InterfaceItem>> = {}
    portalOrder.forEach((portal) => {
      if (res[portal]) {
        Object.keys(res[portal]).map(
          (key) => (res[portal][key].internal = !res[portal][key].link?.startsWith('https://')),
        )
        orderedRes[portal] = res[portal]
      }
    })

    // Add any remaining portals not in order list
    Object.keys(res).forEach((portal) => {
      if (!portalOrder.includes(portal)) {
        orderedRes[portal] = res[portal]
      }
    })

    // Initialize order if not present
    Object.keys(orderedRes).forEach((section) => {
      if (section === 'logos') return
      const items = Object.keys(orderedRes[section])
      items.forEach((key, idx) => {
        if (!orderedRes[section][key].order) {
          orderedRes[section][key].order = idx + 1
        }
      })
    })

    setData(orderedRes)
    setIsLoading(false)
  }

  useEffect(() => {
    initData()
  }, [])

  const onClose = () => {
    initData()
    setSelectedPortal('')
    setEditData(undefined)
  }

  const onChange = async (key: string, subjKey: string, value: boolean) => {
    const temp = cloneDeep(data)
    temp[key][subjKey].value = value

    setIsLoading(true)
    await setAdminConfig('interface', temp)
    dispatch(setMenu(temp))
    setIsLoading(false)
    setData(temp)
  }

  /**
   * The order a drag is building, kept here so it can be written once the drag ends. Sortable
   * reports every row the pointer crosses, so saving on each one would put the whole config
   * through a dozen requests for a single drag.
   */
  const dragSnapshot = useRef<Record<string, Record<string, InterfaceItem>> | null>(null)

  // Renumber the section 1..n in the sequence the drag has reached, so the list redraws in the
  // new order while the pointer is still down. Not saved yet.
  const onReorder = (section: string, newData: { id: string }[]) => {
    const temp = cloneDeep(dragSnapshot.current ?? data)
    newData.forEach((item, index) => (temp[section][item.id].order = index + 1))
    dragSnapshot.current = temp
    setData(temp)
  }

  const onDropReorder = async () => {
    const reordered = dragSnapshot.current
    dragSnapshot.current = null
    // A drag that never crossed another row leaves nothing to save.
    if (!reordered) return

    setIsLoading(true)
    await setAdminConfig('interface', reordered)
    dispatch(setMenu(reordered))
    setIsLoading(false)
  }

  const onDelete = async (section: string, item: any) => {
    const content = (
      <div className="text-gray-400 mb-4 text-[18px]">
        Are you sure to remove this section?
        <br />
      </div>
    )
    const result = await confirm(content)
    if (!result) {
      return
    } else {
      const temp = cloneDeep(data)
      const deletedOrder = temp[section][item.key].order
      delete temp[section][item.key]

      // Reorder remaining items
      Object.keys(temp[section]).forEach((key) => {
        if (temp[section][key].order > deletedOrder) {
          temp[section][key].order--
        }
      })

      setData(temp)
      await setAdminConfig('interface', temp)
      dispatch(setMenu(temp))
    }
  }

  const getSortedItems = (section: string) => {
    return Object.entries(data[section])
      .map(([key, value]) => ({ key, ...(value as InterfaceItem) }))
      .sort((a, b) => a.order - b.order)
  }

  const onChangeLogo = (section: string, value: any) => {
    const temp = cloneDeep(data)
    temp.logos[section] = value
    setData(temp)
  }

  const onSubmit = async () => {
    setIsLoading(true)
    await setAdminConfig('interface', data)
    setIsLoading(false)
  }

  return (
    <div className="relative px-3 py-1.5">
      <LayoutLoading show={isLoading} />
      <div>
        <Button onClick={() => setShowRegisterFormConfig(true)} link>
          Update Register Form
        </Button>
      </div>
      {Object.keys(data).map((section) => {
        if (section === 'logos') return
        const sectionTitle = convertToTitleCase(section)
        const items = getSortedItems(section)
        const sortableData = items.map((item) => ({ id: item.key, data: item }))

        return (
          <div className="mb-4" key={section}>
            <RenderInput
              input={{
                inputType: 'section',
                title: sectionTitle,
                additionalElements: (
                  <div className="flex gap-2">
                    <p className="btn-icon" onClick={() => setSelectedPortal(section)}>
                      <PlusIcon className="w-4 h-4" />
                    </p>
                  </div>
                ),
              }}
              Key={sectionTitle}
              onChange={() => {}}
            />
            {!NO_LOGO_SECTIONS.includes(section) && (
              <div className="my-2">
                <RenderInput
                  input={{
                    inputType: 'text',
                    title: 'Custom Logo Link',
                    value: data.logos?.[section] || '',
                  }}
                  Key={`${section}-logo`}
                  onChange={(key: string, value: any) => onChangeLogo(section, value)}
                  onBlur={onSubmit}
                />
              </div>
            )}
            <div className="text-sm text-gray-600 mb-2">{HELPER_TEXT[section] || HELPER_TEXT.default}</div>
            <Sortable
              data={sortableData}
              setData={(newData: { id: string }[]) => onReorder(section, newData)}
              onDrop={onDropReorder}
              renderItem={(item: any, index: number) => (
                <div
                  className={`flex items-center justify-between gap-4 bg-gray-50 border border-gray-300 rounded pl-3 pr-2 py-2 mb-2 hover:bg-gray-100 ${
                    item.value === false ? 'opacity-60' : ''
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Tooltip message="Drag to reorder">
                      <Bars3Icon className="w-4 h-4 text-gray-500 flex-shrink-0 cursor-move" />
                    </Tooltip>
                    <p className={item.value === false ? 'line-through' : ''}>
                      {index + 1}. {item.title}
                    </p>
                    <p
                      className="underline cursor-pointer text-shade-blue truncate"
                      onClick={() => {
                        if (item.link && item.link.length > 0) window.open(item.link)
                      }}
                    >
                      {item.link ?? ''}
                    </p>
                  </div>
                  <div className="flex gap-2 items-center flex-shrink-0">
                    <Tooltip message="Rename, or point it somewhere else">
                      <span
                        className="btn-icon cursor-pointer"
                        onClick={() => {
                          setSelectedPortal(section)
                          setEditData({ key: item.key, data: item })
                        }}
                      >
                        <PencilSquareIcon className="w-4 h-4" />
                      </span>
                    </Tooltip>
                    <Tooltip message={item.value ? 'Hide from the menu' : 'Show in the menu'}>
                      <span
                        className="btn-icon cursor-pointer"
                        onClick={() => onChange(section, item.key, !item.value)}
                      >
                        {item.value ? <EyeIcon className="w-4 h-4" /> : <EyeSlashIcon className="w-4 h-4" />}
                      </span>
                    </Tooltip>
                    <Tooltip message="Remove from the menu">
                      <p className="btn-icon !text-red-400" onClick={() => onDelete(section, item)}>
                        <TrashIcon className="w-4 h-4" />
                      </p>
                    </Tooltip>
                  </div>
                </div>
              )}
            />
          </div>
        )
      })}
      {selectedPortal !== '' && (
        <InterfaceAddSectionModal
          portal={selectedPortal}
          origin={data}
          onClose={() => onClose()}
          editData={editData}
          isEdit={!!editData}
        />
      )}
      {showRegisterFormConfig && <RegisterFormConfiguration onClose={() => setShowRegisterFormConfig(false)} />}
    </div>
  )
}
